/**
 * ParentReviewPanel — Phase 7-IA Slice 5.
 *
 * A "Review your info" / "Review sponsor info" destination mounted
 * when `currentSectionId` is one of the reserved-prefix summary leaf
 * ids (`__review_about_you`, `__review_sponsor`).
 *
 * The panel surfaces three groups, in this order:
 *
 *   1. Required questions left — every required+visible field across
 *      the parent's sections that is currently unfilled or
 *      validation-dirty. Each row jump-links to the owning section
 *      and (best-effort) focuses the field via `[data-field-id]`.
 *      When zero gaps remain, a green "all required answered" line
 *      replaces the list.
 *   2. Filled answers — for each section, every visible field that
 *      has a non-empty answer, listed under the section heading with
 *      an "Edit section" button. Fields are surfaced via the parent
 *      App's `getAnswerForField` so this component does not depend on
 *      the engine directly — keeps tests stub-friendly.
 *   3. Optional unfilled — collapsed under a native `<details>`
 *      toggle, listing every visible non-required field without an
 *      answer. Hidden when zero such fields exist.
 *
 * The leaf is OPTIONAL — there is no gate before "Print & mail". It
 * is purely a destination users may visit to scan their progress.
 *
 * Structured-value rendering (addresses, names, document uploads) is
 * delegated to `stringifyAnswer` upstream — the App composes a
 * one-line human-readable string per field id and passes it in via
 * `getAnswerForField`. The panel itself never sees raw bag values, so
 * it stays UI-only and cheap to test.
 */
import type { FieldDefinition, FieldId } from '@inside-us-aos/domain';
import { isFieldRequired } from '@inside-us-aos/domain';
import type { SectionProgress } from '@inside-us-aos/form-engine';

import {
  addressSuffix,
  formatAddressBag,
  getAddressClusterKey,
} from './addressClustering';
import { getSectionDisplayLabel } from './sections';

import styles from './ParentReviewPanel.module.css';

/**
 * Title-cases an address bag name (e.g. `mailing_address` →
 * `Mailing address`). Mirrors SectionRenderer's legend logic so the
 * review panel and the editing surface read the same.
 */
function formatBagLabel(bagPrefix: string): string {
  const segments = bagPrefix.split('.');
  const bagName = segments[segments.length - 1];
  const words = bagName.split('_');
  if (words.length === 0) return bagName;
  const [first, ...rest] = words;
  const head = first.length > 0 ? first[0].toUpperCase() + first.slice(1) : '';
  return [head, ...rest].join(' ');
}

export interface IParentReviewPanelProps {
  /** Owning parent group id (`'about_you'` or `'about_sponsor'`). */
  parentGroupId: string;
  /** Human-readable parent label rendered as the panel heading. */
  parentLabel: string;
  /** Sections to summarize, in render order. */
  sectionIds: readonly string[];
  /**
   * Engine handle: per-section progress lookup. Returning `undefined`
   * collapses the row's contribution silently (defensive).
   */
  getSectionProgress: (sectionId: string) => SectionProgress | undefined;
  /**
   * Engine handle: stringified answer for a field id, or `undefined`
   * when no answer is present. Multi-component answers (address /
   * name / document_upload / select) are formatted upstream via
   * `stringifyAnswer` so this component only ever sees a finished
   * one-line string.
   */
  getAnswerForField: (fieldId: FieldId) => string | undefined;
  /**
   * Visible fields per section. The App pre-filters by
   * `engine.getFieldStatus(...).visible` so the panel never has to
   * re-evaluate visibility.
   */
  fieldsBySectionId: ReadonlyMap<string, readonly FieldDefinition[]>;
  /** Jump to a specific field — typically scrolls + focuses it. */
  onJumpToField: (sectionId: string, fieldId: FieldId) => void;
  /** Jump to a section (no field-level focus). */
  onEditSection: (sectionId: string) => void;
}

interface IGapRow {
  sectionId: string;
  sectionLabel: string;
  fieldId: FieldId;
  fieldLabel: string;
}

interface IFilledRow {
  fieldId: FieldId;
  fieldLabel: string;
  answer: string;
}

interface IOptionalRow {
  sectionId: string;
  sectionLabel: string;
  fieldId: FieldId;
  fieldLabel: string;
}

export function ParentReviewPanel(
  props: IParentReviewPanelProps,
): JSX.Element {
  const {
    parentGroupId,
    parentLabel,
    sectionIds,
    getSectionProgress,
    getAnswerForField,
    fieldsBySectionId,
    onJumpToField,
    onEditSection,
  } = props;

  // Required gaps — flatten across every section in render order.
  const gapRows: IGapRow[] = [];
  for (const sectionId of sectionIds) {
    const progress = getSectionProgress(sectionId);
    if (!progress) continue;
    const sectionLabel = getSectionDisplayLabel(sectionId);
    const fields = fieldsBySectionId.get(sectionId) ?? [];
    const fieldById = new Map<string, FieldDefinition>(
      fields.map(f => [f.id, f]),
    );
    for (const fieldId of progress.missingFieldIds) {
      const def = fieldById.get(fieldId);
      gapRows.push({
        sectionId,
        sectionLabel,
        fieldId,
        fieldLabel: def?.label ?? fieldId,
      });
    }
  }

  // Filled rows + optional-unfilled rows, both per section.
  //
  // Address bags fold into a single filled row
  // per bag instead of one row per sibling sub-field. The bag's
  // suffix-keyed answer map drives `formatAddressBag`, which produces
  // the canonical one-liner. Non-address fields render one row each
  // as before.
  const filledBySection = new Map<string, IFilledRow[]>();
  const optionalRows: IOptionalRow[] = [];
  for (const sectionId of sectionIds) {
    const filled: IFilledRow[] = [];
    const fields = fieldsBySectionId.get(sectionId) ?? [];
    const sectionLabel = getSectionDisplayLabel(sectionId);
    const bagsAnchored = new Set<string>();
    for (const field of fields) {
      const bagKey = getAddressClusterKey(field);
      if (bagKey !== null) {
        if (bagsAnchored.has(bagKey)) continue;
        bagsAnchored.add(bagKey);
        // Collect every sibling in this section that maps to the
        // same bag. Build a suffix-keyed answer map and let
        // formatAddressBag compose the one-liner.
        const siblings = fields.filter(
          f => getAddressClusterKey(f) === bagKey,
        );
        const answersBySuffix = new Map<string, string>();
        for (const sibling of siblings) {
          const answer = getAnswerForField(sibling.id);
          if (answer !== undefined && answer.length > 0) {
            answersBySuffix.set(addressSuffix(sibling.id as string), answer);
          }
        }
        const composed = formatAddressBag(answersBySuffix);
        if (composed !== undefined) {
          filled.push({
            fieldId: field.id,
            fieldLabel: formatBagLabel(bagKey),
            answer: composed,
          });
        } else {
          // Bag empty — count any non-required siblings as optional
          // unfilled so the disclosure surfaces them. Required-but-
          // empty siblings are already in the gap list via
          // `progress.missingFieldIds`.
          for (const sibling of siblings) {
            if (!isFieldRequired(sibling)) {
              optionalRows.push({
                sectionId,
                sectionLabel,
                fieldId: sibling.id,
                fieldLabel: sibling.label,
              });
            }
          }
        }
        continue;
      }
      const answer = getAnswerForField(field.id);
      if (answer !== undefined && answer.length > 0) {
        filled.push({
          fieldId: field.id,
          fieldLabel: field.label,
          answer,
        });
      } else if (!isFieldRequired(field)) {
        optionalRows.push({
          sectionId,
          sectionLabel,
          fieldId: field.id,
          fieldLabel: field.label,
        });
      }
    }
    if (filled.length > 0) {
      filledBySection.set(sectionId, filled);
    }
  }

  return (
    <section
      className={`${styles.panel} reveal_on_mount`}
      aria-label={parentLabel}
      data-parent-group-id={parentGroupId}
    >
      <header className={styles.panel__header}>
        <p className={styles.panel__eyebrow}>Review</p>
        <h2 className={styles.panel__heading}>{parentLabel}</h2>
      </header>

      {gapRows.length > 0 ? (
        <div
          className={styles.panel__gaps}
          role="region"
          aria-label="Required questions left"
        >
          <div className={styles.panel__gaps_header}>
            <span className={styles.panel__gaps_dot} aria-hidden="true" />
            <h3 className={styles.panel__gaps_heading}>
              {gapRows.length} required question{gapRows.length === 1 ? '' : 's'} left
            </h3>
          </div>
          <ul className={styles.panel__gap_list}>
            {gapRows.map(row => (
              <li
                key={`${row.sectionId}::${row.fieldId}`}
                className={styles.panel__gap_row}
                data-field-id={row.fieldId}
              >
                <span className={styles.panel__gap_label}>
                  <span className={styles.panel__gap_section}>
                    {row.sectionLabel}
                  </span>
                  <span className={styles.panel__gap_field}>
                    {row.fieldLabel}
                  </span>
                </span>
                <button
                  type="button"
                  className={styles.panel__link_button}
                  onClick={() => onJumpToField(row.sectionId, row.fieldId)}
                >
                  Jump to it →
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className={styles.panel__gaps_clean} role="status">
          <span className={styles.panel__gaps_dot_clean} aria-hidden="true" />
          All required questions answered
        </p>
      )}

      <div className={styles.panel__sections}>
        {sectionIds.map(sectionId => {
          const filled = filledBySection.get(sectionId) ?? [];
          if (filled.length === 0) return null;
          const sectionLabel = getSectionDisplayLabel(sectionId);
          return (
            <div
              key={sectionId}
              className={styles.panel__section}
              data-section-id={sectionId}
            >
              <div className={styles.panel__section_header}>
                <h3 className={styles.panel__section_heading}>
                  {sectionLabel}
                </h3>
                <button
                  type="button"
                  className={styles.panel__link_button}
                  onClick={() => onEditSection(sectionId)}
                >
                  Edit section →
                </button>
              </div>
              <dl className={styles.panel__answers}>
                {filled.map(row => (
                  <div key={row.fieldId} className={styles.panel__answer_row}>
                    <dt className={styles.panel__answer_label}>
                      {row.fieldLabel}
                    </dt>
                    <dd className={styles.panel__answer_value}>
                      {row.answer}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}
      </div>

      {optionalRows.length > 0 ? (
        <details className={styles.panel__optional}>
          <summary className={styles.panel__optional_summary}>
            Show {optionalRows.length} optional question
            {optionalRows.length === 1 ? '' : 's'} you haven&apos;t answered
          </summary>
          <ul className={styles.panel__optional_list}>
            {optionalRows.map(row => (
              <li
                key={`${row.sectionId}::${row.fieldId}`}
                className={styles.panel__optional_row}
                data-field-id={row.fieldId}
              >
                <span className={styles.panel__gap_label}>
                  <span className={styles.panel__gap_section}>
                    {row.sectionLabel}
                  </span>
                  <span className={styles.panel__gap_field}>
                    {row.fieldLabel}
                  </span>
                </span>
                <button
                  type="button"
                  className={styles.panel__link_button}
                  onClick={() => onJumpToField(row.sectionId, row.fieldId)}
                >
                  Jump to it →
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
